import React from "react";
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";

const COLORS = ["#0d6efd", "#198754", "#dc3545", "#ffc107", "#6610f2", "#0dcaf0", "#fd7e14", "#20c997"];

function getAnswers(rows, questionNo) {
  return rows
    .filter(r => r.status !== "SKIPPED")
    .map(r => r.answers?.[String(questionNo)])
    .filter(Boolean);
}

function RadioSummary({ question, rows }) {
  const counts = {};
  getAnswers(rows, question.no).forEach(ans => {
    if (ans.value) counts[ans.value] = (counts[ans.value] || 0) + 1;
  });
  const data = Object.entries(counts).map(([name, value]) => ({ name, value }));
  if (data.length === 0) return <div className="text-muted small">No answers</div>;
  return (
    <ResponsiveContainer width="100%" height={220}>
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75}
          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
          labelLine={false}>
          {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
        </Pie>
        <Tooltip />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
}

function CheckSummary({ question, rows }) {
  const hasRealChoices = question.choices.some(c => c.text && c.text.trim());
  const answers = getAnswers(rows, question.no);

  if (hasRealChoices) {
    const counts = {};
    answers.forEach(ans => {
      if (!ans.value) return;
      ans.value.split(", ").forEach(v => {
        counts[v] = (counts[v] || 0) + 1;
      });
    });
    const data = Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ name, count }));
    if (data.length === 0) return <div className="text-muted small">No answers</div>;
    return (
      <ResponsiveContainer width="100%" height={Math.max(160, data.length * 36)}>
        <BarChart data={data} layout="vertical" margin={{ left: 8, right: 24 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" allowDecimals={false} />
          <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 12 }} />
          <Tooltip />
          <Bar dataKey="count" fill="#0d6efd" radius={[0, 3, 3, 0]}>
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  }

  const responses = answers.map(a => a.value).filter(Boolean);
  if (responses.length === 0) return <div className="text-muted small">No answers</div>;
  return <ResponseList responses={responses} />;
}

function TextSummary({ question, rows }) {
  const responses = getAnswers(rows, question.no).map(a => a.value).filter(Boolean);
  if (responses.length === 0) return <div className="text-muted small">No answers</div>;
  return <ResponseList responses={responses} />;
}

function PhotoSummary({ question, rows }) {
  const count = getAnswers(rows, question.no).filter(a => a.fileUrl).length;
  return <div className="text-muted small pt-2">{count} photo{count !== 1 ? "s" : ""} taken</div>;
}

function ResponseList({ responses }) {
  return (
    <ul className="list-group list-group-flush" style={{ maxHeight: 220, overflowY: "auto" }}>
      {responses.map((r, i) => (
        <li key={i} className="list-group-item py-1" style={{ fontSize: "0.82rem" }}>{r}</li>
      ))}
    </ul>
  );
}

export default function TaskReportSummary({ report }) {
  const completed = report.rows.filter(r => r.status !== "SKIPPED");
  const skipped = report.rows.filter(r => r.status === "SKIPPED");

  return (
    <div>
      <div className="d-flex gap-3 mb-4">
        <div className="card text-center px-4 py-3">
          <div className="fs-3 fw-bold text-primary">{completed.length}</div>
          <div className="text-muted small">Completed</div>
        </div>
        {skipped.length > 0 && (
          <div className="card text-center px-4 py-3">
            <div className="fs-3 fw-bold text-warning">{skipped.length}</div>
            <div className="text-muted small">Skipped</div>
          </div>
        )}
      </div>

      <div className="row g-4">
        {report.questions.map(q => (
          <div key={q.no} className={q.type === "TEXT" || (q.type === "CHECK" && !q.choices.some(c => c.text?.trim())) ? "col-12" : "col-md-6"}>
            <div className="card h-100 shadow-sm">
              <div className="card-header py-2 d-flex justify-content-between align-items-center">
                <span className="small fw-semibold">{q.text}</span>
                <span className="badge bg-secondary ms-2" style={{ fontSize: "0.7rem" }}>{q.type}</span>
              </div>
              <div className="card-body">
                {q.type === "RADIO" && <RadioSummary question={q} rows={report.rows} />}
                {q.type === "CHECK" && <CheckSummary question={q} rows={report.rows} />}
                {q.type === "TEXT"  && <TextSummary  question={q} rows={report.rows} />}
                {q.type === "PHOTO" && <PhotoSummary question={q} rows={report.rows} />}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
